import { Id, LaravelResponse, keyToUrl } from "@dgs/core";
import axios from "axios";
import {
	IConfirmReplacementResource,
	IConfirmResource,
	IConsent,
	IRegistrationFormShowResource,
	PaymentProviderOptionResource,
	RegistrationFormEmailGateRequest,
	RegistrationFormEmailGateResource,
	RegistrationFormResource,
	RegistrationResponse,
	RegistrationShopSummaryResource,
	RegistrationSummaryNotAvailableResource,
} from "../../types";

export class RegistrationFormService {
	public keys = {
		details: (id: Id) => ["registration-forms", `${id}`],
		shopSummary: (id: Id) => ["registration-forms", `${id}`, "shop-summary"],
		cancellationSummary: (registrationFormId: Id, guestId: Id) => [
			"registration-forms",
			`${registrationFormId}`,
			"cancellation-shop-summary",
			`${guestId}`,
		],
		previewDetails: (id: Id) => ["registration-forms", "preview", `${id}`],
		cancel: (id: Id) => ["registration-forms", `${id}`, "cancel"],
		decline: (id: Id) => ["registration-forms", `${id}`, "decline"],
		consentsForLandingPage: (id: Id) => ["registration-forms", `${id}`, "consents", "landing-page"],
		consentsForSummaryPage: (id: Id) => ["registration-forms", `${id}`, "consents", "summary-page"],
		consentsForEmailGatePage: (id: Id) => ["registration-forms", `${id}`, "consents", "email-gate"],
		paymentProviders: (id: Id) => ["registration-forms", `${id}`, "shop", "payment-providers"],
		showConfirmDeclination: (id: Id) => ["registration-forms", `${id}`, "confirm-declination"],
		showConfirmCancellation: (id: Id) => ["registration-forms", `${id}`, "confirm-cancellation"],
		nominateReplacement: (id: Id) => ["registration-forms", `${id}`, "nominate-replacement"],
		showConfirmReplacement: (id: Id) => ["registration-forms", `${id}`, "confirm-replacement"],
		verifyCode: (id: Id) => ["registration-forms", `${id}`, "verify-code"],
		emailGate: (id: Id) => ["registration-forms", `${id}`, "email-gate"],
	};
	protected urls = {
		details: (id: Id) => keyToUrl(this.keys.details(id)),
		shopSummary: (id: Id) => keyToUrl(this.keys.shopSummary(id)),
		cancellationSummary: (registrationFormId: Id, guestId: Id) =>
			keyToUrl(this.keys.cancellationSummary(registrationFormId, guestId)),
		previewDetails: (id: Id) => keyToUrl(this.keys.previewDetails(id)),
		consentForLandingPage: (formId: Id) => keyToUrl(this.keys.consentsForLandingPage(formId)),
		consentForSummaryPage: (formId: Id) => keyToUrl(this.keys.consentsForSummaryPage(formId)),
		consentForEmailGatePage: (formId: Id) => keyToUrl(this.keys.consentsForEmailGatePage(formId)),
		cancel: (formId: Id) => keyToUrl(this.keys.cancel(formId)),
		decline: (formId: Id) => keyToUrl(this.keys.decline(formId)),
		paymentProviders: (formId: Id) => keyToUrl(this.keys.paymentProviders(formId)),
		showConfirmDeclination: (formId: Id) => keyToUrl(this.keys.showConfirmDeclination(formId)),
		showConfirmCancellation: (formId: Id) => keyToUrl(this.keys.showConfirmCancellation(formId)),
		nominateReplacement: (formId: Id) => keyToUrl(this.keys.nominateReplacement(formId)),
		showConfirmReplacement: (formId: Id) => keyToUrl(this.keys.showConfirmReplacement(formId)),
		verifyCode: (formId: Id) => keyToUrl(this.keys.verifyCode(formId)),
		emailGate: (formId: Id) => keyToUrl(this.keys.emailGate(formId)),
	};

	show = (id: Id, token: string | null, regionId: Id | null) =>
		axios.get<LaravelResponse<IRegistrationFormShowResource>>(this.urls.details(id), {
			params: {
				token,
				regionId,
			},
		});

	previewShow = (id: Id, regionId: Id | null) => {
		return axios.get<LaravelResponse<RegistrationFormResource>>(this.urls.previewDetails(id), {
			params: {
				regionId,
			},
		});
	};

	consentsForLandingPage = (id: Id, regionId: Id | null, emailDomain?: string) => {
		return axios.get<LaravelResponse<IConsent[]>>(this.urls.consentForLandingPage(id), {
			params: { regionId, emailDomain },
		});
	};

	consentsForSummaryPage = (id: Id, regionId: Id | null, emailDomain?: string) => {
		return axios.get<LaravelResponse<IConsent[]>>(this.urls.consentForSummaryPage(id), {
			params: { regionId, emailDomain },
		});
	};

	consentsForEmailGatePage = (id: Id, regionId: Id | null, emailDomain?: string) => {
		return axios.get<LaravelResponse<IConsent[]>>(this.urls.consentForEmailGatePage(id), {
			params: { regionId, emailDomain },
		});
	};

	getPaymentProviders = (formId: Id) =>
		axios.get<LaravelResponse<PaymentProviderOptionResource[]>>(this.urls.paymentProviders(formId));

	showConfirmDeclination = (formId: Id) =>
		axios.get<LaravelResponse<IConfirmResource>>(this.urls.showConfirmDeclination(formId));

	showConfirmCancellation = (formId: Id) =>
		axios.get<LaravelResponse<IConfirmResource>>(this.urls.showConfirmCancellation(formId));

	showConfirmReplacement = (formId: Id) =>
		axios.get<LaravelResponse<IConfirmReplacementResource>>(this.urls.showConfirmReplacement(formId));

	cancelRegistration = (
		formId: Id,
		dataFieldValues: { id: number; value: any }[],
		registrationLocale: string,
		regionId: Id | null,
		token: string | undefined,
	) =>
		axios.post<LaravelResponse<void>>(this.urls.cancel(formId), {
			dataFieldValues,
			token,
			registrationLocale,
			regionId,
		});

	declineRegistration = (
		formId: Id,
		dataFieldValues: { id: number; value: any }[],
		registrationLocale: string,
		regionId: Id | null,
		token: string | undefined,
	) =>
		axios.post<LaravelResponse<void>>(this.urls.decline(formId), {
			dataFieldValues,
			token,
			registrationLocale,
			regionId,
		});

	nominateReplacement = (formId: Id, dataFieldValues: { id: number; value: any }[], token: string | undefined) =>
		axios.post<LaravelResponse<void>>(this.urls.nominateReplacement(formId), { dataFieldValues, token });

	verifyCode = (formId: Id, registrationCode: string) =>
		axios.post<LaravelResponse<RegistrationFormResource>>(this.urls.verifyCode(formId), {
			registrationCode,
		});

	submitRegistrationFormData = (formId: Id, data: FormData) =>
		axios.post<LaravelResponse<RegistrationResponse>>(this.urls.details(formId), data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

	submitEmailGate = (formId: Id, request: RegistrationFormEmailGateRequest) =>
		axios.post<LaravelResponse<RegistrationFormEmailGateResource>>(this.urls.emailGate(formId), request);

	submitRegistrationFormDataForShopSummary = (formId: Id, data: FormData) =>
		axios.post<LaravelResponse<RegistrationShopSummaryResource | RegistrationSummaryNotAvailableResource>>(
			this.urls.shopSummary(formId),
			data,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			},
		);

	getCancellationSummary = (formId: Id, guestId: Id) =>
		axios.get<LaravelResponse<RegistrationShopSummaryResource>>(this.urls.cancellationSummary(formId, guestId));
}

export const registrationFormService = new RegistrationFormService();
